import { useState, useCallback, useRef } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, FileCode, Loader2 } from 'lucide-react';

interface GuidedWelcomeProps {
  onStart: (idea: string, name?: string) => void;
  onBrowseTemplates: () => void;
  isProcessing: boolean;
}

export function GuidedWelcome({ onStart, onBrowseTemplates, isProcessing }: GuidedWelcomeProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [idea, setIdea] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState<string | null>(null);
  const [isCheckingName, setIsCheckingName] = useState(false);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  const checkName = useCallback(async (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      setNameError(null);
      return;
    }

    setIsCheckingName(true);
    setNameError(null);

    try {
      const result = await api.checkArcheName(trimmed);
      if (!result.available) {
        setNameError('This name is already taken');
      }
    } catch {
      setNameError('Failed to check name availability');
    } finally {
      setIsCheckingName(false);
    }
  }, []);

  const handleSubmit = () => {
    const trimmed = idea.trim();
    if (!trimmed || isProcessing || nameError) return;
    onStart(trimmed, name.trim() || undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6 max-w-lg mx-auto">
      <div className="text-center space-y-2">
        <Sparkles className="h-8 w-8 mx-auto text-primary" />
        <h2 className="text-xl font-semibold">What would you like to build?</h2>
        <p className="text-sm text-muted-foreground">
          Describe your idea and we'll guide you through the entire build process.
        </p>
      </div>

      <Textarea
        ref={textareaRef}
        value={idea}
        onChange={(e) => {
          setIdea(e.target.value);
          autoResize();
        }}
        onKeyDown={handleKeyDown}
        placeholder="I want to build an Arche that..."
        className="min-h-[100px] resize-none overflow-hidden"
        disabled={isProcessing}
        autoFocus
      />

      <div className="w-full space-y-1">
        <label htmlFor="arche-name" className="text-sm font-medium">
          What do you want to name your Arche?
        </label>
        <div className="relative">
          <Input
            id="arche-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setNameError(null);
            }}
            onBlur={() => checkName(name)}
            placeholder="My Custom Arche"
            disabled={isProcessing}
            className={nameError ? 'border-destructive' : ''}
          />
          {isCheckingName && (
            <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          )}
        </div>
        {nameError && <p className="text-sm text-destructive">{nameError}</p>}
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!idea.trim() || isProcessing || !!nameError}
        className="w-full"
        size="lg"
      >
        Start Building
      </Button>

      {/* Build from template */}
      <div className="space-y-2 w-full text-center">
        <p className="text-xs text-muted-foreground">Or build from a template</p>
        <Button
          variant="outline"
          onClick={onBrowseTemplates}
          disabled={isProcessing}
          className="w-full"
        >
          <FileCode className="h-4 w-4 mr-2" />
          Browse Templates
        </Button>
      </div>
    </div>
  );
}
